import { Alert, Checkbox, DatePicker, Form, Input, InputNumber, Modal, Select, TimePicker } from "antd";
import dayjs from "dayjs";
import { isEmpty, isEqual, sortBy } from "lodash-es";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import type { InfinitySelectOption } from "~components/InfinitySelect";
import { SelectPersons } from "~components/SelectPersons/SelectPersons";
import { SelectPool } from "~components/SelectPool/SelectPool";
import { SelectSeasonEntity } from "~components/SelectSeasonEntity/SelectSeasonEntity";
import { SelectSeries } from "~components/SelectSeries/SelectSeries";
import { SelectStage } from "~components/SelectStage/SelectStage";
import { SelectVenue } from "~components/SelectVenue/SelectVenue";
import { CompetitorTypes } from "~enums/competitorTypes";
import { FixtureStatuses } from "~enums/fixtureStatuses";
import { FixtureTypes } from "~enums/fixtureTypes";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import { useCreateFixture, useUpdateListFixture } from "../hooks";
import type { FixtureTransformed } from "../types";
import { isFixtureEditable } from "../utilities";

interface IUpsertFixtureModalProperties {
  fixture?: FixtureTransformed;
  onClose: () => void;
}

export const UpsertFixtureModal = (properties: IUpsertFixtureModalProperties) => {
  const [form] = Form.useForm();
  const { formatMessage } = useIntl();
  const { mutateAsync: createFixture, isPending: isCreatingFixtureInProcess } = useCreateFixture();
  const { mutateAsync: updateFixture, isPending: isUpdatingFixtureInProcess } = useUpdateListFixture();
  const sportConfiguration = useSportConfiguration();
  const [homeCompetitorDisabledOptionValues, setHomeCompetitorDisabledOptionValues] = useState<Array<string>>(
    isEmpty(properties.fixture?.awayCompetitor?.entity?.entityId) ? [] : [properties.fixture?.awayCompetitor?.entity?.entityId],
  );
  const [awayCompetitorDisabledOptionValues, setAwayCompetitorDisabledOptionValues] = useState<Array<string>>(
    isEmpty(properties.fixture?.homeCompetitor?.entity?.entityId) ? [] : [properties.fixture?.homeCompetitor?.entity?.entityId],
  );
  const [homeParticipantsDisabledOptionValues, setHomeParticipantsDisabledOptionValues] = useState<Array<string>>(
    (properties.fixture?.awayParticipants || []).map((x) => x.personId),
  );
  const [awayParticipantsDisabledOptionValues, setAwayParticipantsDisabledOptionValues] = useState<Array<string>>(
    (properties.fixture?.homeParticipants || []).map((x) => x.personId),
  );
  const isEdit = !isEmpty(properties.fixture);
  const [createAnother, setCreateAnother] = useState<boolean>(false);

  const formInitValues = useMemo(() => {
    return {
      roundNumber: properties.fixture?.roundNumber,
      nameLocal: properties.fixture?.nameLocal,
      fixtureNumber: properties.fixture?.fixtureNumber,
      homeCompetitor: isEmpty(properties.fixture?.homeCompetitor?.entity)
        ? null
        : {
            label: properties.fixture?.homeCompetitor?.entity?.nameFullLocal,
            value: properties.fixture?.homeCompetitor?.entity?.entityId,
          },
      awayCompetitor: isEmpty(properties.fixture?.awayCompetitor?.entity)
        ? null
        : {
            label: properties.fixture?.awayCompetitor.entity.nameFullLocal,
            value: properties.fixture?.awayCompetitor.entity.entityId,
          },
      homeParticipants:
        properties.fixture?.homeParticipants
          ?.filter((x) => !isEmpty(x.person?.personId))
          .map((x) => ({
            label: x.person.nameFullLocal,
            value: x.person.personId,
          })) ?? [],
      awayParticipants:
        properties.fixture?.awayParticipants
          ?.filter((x) => !isEmpty(x.person?.personId))
          .map((x) => ({
            label: x.person.nameFullLocal,
            value: x.person.personId,
          })) ?? [],
      venue: isEmpty(properties.fixture?.venue?.venueId)
        ? null
        : {
            label: properties.fixture?.venue.nameLocal,
            value: properties.fixture?.venue.venueId,
          },
      startDate: isEmpty(properties.fixture?.startDate?.split("T")?.[0])
        ? null
        : dayjs(properties.fixture?.startDate?.split("T")?.[0], "YYYY-MM-DD"),
      startTime:
        properties.fixture?.timesUnconfirmed || isEmpty(properties.fixture?.startTime?.split("T")?.[1])
          ? null
          : dayjs(properties.fixture?.startTime?.split("T")?.[1], "HH:mm"),
      timesUnconfirmed: properties.fixture?.timesUnconfirmed ?? false,
      fixtureType: properties.fixture?.fixtureType ?? FixtureTypes.Regular,
      status: properties.fixture?.status ?? FixtureStatuses.Draft,
      stage: isEmpty(properties.fixture?.stage?.stageCode)
        ? null
        : {
            label: properties.fixture?.stage.nameLocal,
            value: properties.fixture?.stage.stageCode,
          },
      pool: isEmpty(properties.fixture?.pool?.poolCode)
        ? null
        : {
            label: properties.fixture?.pool.nameLocal,
            value: properties.fixture?.pool.poolCode,
          },
      series: isEmpty(properties.fixture?.series?.seriesCode)
        ? null
        : {
            label: properties.fixture?.series.nameLocal,
            value: properties.fixture?.series.seriesCode,
          },
    };
  }, [properties]);

  const onHomeCompetitorChangedHandler = (competitorOption: InfinitySelectOption | null) => {
    setAwayCompetitorDisabledOptionValues(competitorOption?.value ? [competitorOption.value] : []);
  };

  const onAwayCompetitorChangedHandler = (competitorOption: InfinitySelectOption | null) => {
    setHomeCompetitorDisabledOptionValues(competitorOption?.value ? [competitorOption.value] : []);
  };

  const onHomeParticipantsChangedHandler = (participantOptions: InfinitySelectOption[]) => {
    setAwayParticipantsDisabledOptionValues(participantOptions.map((x) => x.value));
  };

  const onAwayParticipantsChangedHandler = (participantOptions: InfinitySelectOption[]) => {
    setHomeParticipantsDisabledOptionValues(participantOptions.map((x) => x.value));
  };

  const clearFormValuesForCreateAnother = () => {
    form.setFieldsValue({
      roundNumber: null,
      nameLocal: null,
      fixtureNumber: null,
      homeCompetitor: null,
      awayCompetitor: null,
      homeParticipants: [],
      awayParticipants: [],
      startDate: null,
      startTime: null,
      fixtureType: FixtureTypes.Regular,
      status: FixtureStatuses.Draft,
      stage: null,
      pool: null,
      series: null,
    });
    setAwayCompetitorDisabledOptionValues([]);
    setHomeCompetitorDisabledOptionValues([]);
    setAwayParticipantsDisabledOptionValues([]);
    setHomeParticipantsDisabledOptionValues([]);
  };

  const saveFixture = async (upsertData: any) => {
    if (isEdit) {
      await updateFixture({
        fixtureId: properties.fixture?.fixtureId,
        upsertData: upsertData,
        oldSeriesCode: properties.fixture?.series?.seriesCode,
      });
    } else {
      await createFixture({ upsertData: upsertData });
    }
    if (createAnother) {
      clearFormValuesForCreateAnother();
    } else {
      properties.onClose();
    }
  };

  const onOkHandler = async () => {
    const dataSource = (await form.validateFields()) ?? {};

    const upsertData: any = {
      competitorType: sportConfiguration.competitorType,
      roundNumber: dataSource.roundNumber?.toString() || null,
      fixtureType: dataSource.fixtureType,
      status: dataSource.status,
      venueId: dataSource.venue?.value ?? null,
      stageCode: dataSource.stage?.value ?? null,
      poolCode: dataSource.pool?.value ?? null,
      seriesCode: dataSource.series?.value ?? null,
      discipline: properties.fixture?.discipline ?? null,
      nameLocal: dataSource.nameLocal ?? null,
      fixtureNumber: dataSource.fixtureNumber ?? null,
    };

    if (dataSource.startDate && dataSource.startTime) {
      upsertData.startTimeLocal = `${dataSource.startDate.format("YYYY-MM-DD")}T${dataSource.startTime.format("HH:mm:ss")}`;
    } else if (dataSource.startDate) {
      upsertData.startTimeLocal = `${dataSource.startDate.format("YYYY-MM-DD")}T00:00:00`;
    } else {
      upsertData.startTimeLocal = null;
    }

    // for the new fixture we don't set timesUnconfirmed, only for update of existing one.
    if (properties.fixture) {
      upsertData.timesUnconfirmed = !dataSource.startTime;
    }

    if (sportConfiguration.competitorType === CompetitorTypes.Entity) {
      const isHomeCompetitorChanged = properties.fixture?.homeCompetitor?.entity?.entityId !== dataSource.homeCompetitor?.value;
      const isAwayCompetitorChanged = properties.fixture?.awayCompetitor?.entity?.entityId !== dataSource.awayCompetitor?.value;

      // Update competitors only when changed because when we update it we reset score and result
      if (isHomeCompetitorChanged || isAwayCompetitorChanged) {
        const competitors: any[] = [];
        if (!isEmpty(dataSource.homeCompetitor?.value)) {
          competitors.push({
            entityId: dataSource.homeCompetitor.value,
            isHome: true,
          });
        }
        if (!isEmpty(dataSource.awayCompetitor?.value)) {
          competitors.push({
            entityId: dataSource.awayCompetitor.value,
            isHome: false,
          });
        }

        upsertData.competitors = competitors;
      }
    }

    if (sportConfiguration.competitorType === CompetitorTypes.Person) {
      const isHomeParticipantsChanged = !isEqual(
        sortBy(properties.fixture?.homeParticipants?.map((x) => x.person?.personId)) ?? [],
        sortBy(dataSource.homeParticipants?.map((x: any) => x.value) ?? []),
      );
      const isAwayParticipantsChanged = !isEqual(
        sortBy(properties.fixture?.awayParticipants?.map((x) => x.person?.personId)) ?? [],
        sortBy(dataSource.awayParticipants?.map((x: any) => x.value) ?? []),
      );

      // Update participants only when changed because when we update it we reset score and result
      if (isHomeParticipantsChanged || isAwayParticipantsChanged) {
        const participants: any[] = [];
        if (!isEmpty(dataSource.homeParticipants)) {
          dataSource.homeParticipants
            .filter((x: any) => !isEmpty(x.value))
            .forEach((x: any) => {
              participants.push({ personId: x.value, isHome: true });
            });
        }
        if (!isEmpty(dataSource.awayParticipants)) {
          dataSource.awayParticipants
            .filter((x: any) => !isEmpty(x.value))
            .forEach((x: any) => {
              participants.push({ personId: x.value, isHome: false });
            });
        }

        upsertData.participants = participants;
      }
    }

    await saveFixture(upsertData);
  };

  return (
    <Modal
      open={true}
      onCancel={properties.onClose}
      okText={formatMessage({ id: isEdit ? "update" : "create" })}
      onOk={onOkHandler}
      okButtonProps={{
        loading: isCreatingFixtureInProcess || isUpdatingFixtureInProcess,
      }}
      footer={(originNode) => {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "end",
              gap: 5,
              flexDirection: "row",
            }}
          >
            <Checkbox style={{ marginLeft: 62 }} checked={createAnother} onChange={(e) => setCreateAnother(e.target.checked)}>
              {formatMessage({ id: "fixtures.add.create.another" })}
            </Checkbox>
            <div style={{ display: "flex", gap: 5 }}>{originNode}</div>
          </div>
        );
      }}
    >
      {!isEmpty(properties.fixture) && !isFixtureEditable(properties.fixture, true) && (
        <Alert
          style={{ marginTop: "24px" }}
          type="warning"
          showIcon={true}
          message={formatMessage({
            id: "fixtureListPage.upsertFixtureModel.notEditable.warning",
          })}
        />
      )}

      <Form form={form} labelCol={{ span: 7 }} wrapperCol={{ span: 18 }} initialValues={formInitValues}>
        <Form.Item
          name={"roundNumber"}
          label={formatMessage({
            id: "fixtureListPage.upsertFixtureModel.form.roundNumber",
          })}
          tooltip={formatMessage({ id: "form.help.fixture.round" })}
          style={{ marginTop: "30px" }}
          rules={[{ max: 30 }]}
        >
          <Input disabled={!isEmpty(properties.fixture) && !isFixtureEditable(properties.fixture, true)} />
        </Form.Item>

        <Form.Item
          name={"nameLocal"}
          label={formatMessage({
            id: "fixtureListPage.upsertFixtureModel.form.name",
          })}
          tooltip={formatMessage({ id: "form.help.local.name.item" }, { item: formatMessage({ id: "match" }) })}
          style={{ marginBottom: "5px" }}
          rules={[{ max: 150 }]}
        >
          <Input disabled={!isEmpty(properties.fixture) && !isFixtureEditable(properties.fixture, true)} />
        </Form.Item>

        <Form.Item
          name={"fixtureNumber"}
          label={formatMessage({ id: "fixture.number" })}
          tooltip={formatMessage({ id: "form.help.fixture.number" })}
          style={{ marginBottom: "5px" }}
        >
          <InputNumber style={{ width: "100%" }} disabled={!isEmpty(properties.fixture) && !isFixtureEditable(properties.fixture, true)} />
        </Form.Item>

        {sportConfiguration.competitorType === CompetitorTypes.Entity && (
          <>
            <Form.Item
              name={"homeCompetitor"}
              label={formatMessage({
                id: "fixtureListPage.upsertFixtureModel.form.homeCompetitor",
              })}
              tooltip={formatMessage({ id: "form.help.fixture.home.team" })}
              style={{ marginBottom: "5px" }}
            >
              <SelectSeasonEntity
                disabled={!isEmpty(properties.fixture) && !isFixtureEditable(properties.fixture, true)}
                placeholder={""}
                onChange={onHomeCompetitorChangedHandler}
                disabledOptionValues={homeCompetitorDisabledOptionValues}
              />
            </Form.Item>

            <Form.Item
              name={"awayCompetitor"}
              label={formatMessage({
                id: "fixtureListPage.upsertFixtureModel.form.awayCompetitor",
              })}
              tooltip={formatMessage({ id: "form.help.fixture.away.team" })}
            >
              <SelectSeasonEntity
                disabled={!isEmpty(properties.fixture) && !isFixtureEditable(properties.fixture, true)}
                placeholder={""}
                onChange={onAwayCompetitorChangedHandler}
                disabledOptionValues={awayCompetitorDisabledOptionValues}
              />
            </Form.Item>
          </>
        )}

        {sportConfiguration.competitorType === CompetitorTypes.Person && (
          <>
            <Form.Item
              name={"homeParticipants"}
              label={formatMessage({
                id: "fixtureListPage.upsertFixtureModel.form.homeParticipants",
              })}
              tooltip={formatMessage({ id: "form.help.fixture.home.persons" })}
              style={{ marginBottom: "5px" }}
            >
              <SelectPersons
                disabled={!isEmpty(properties.fixture) && !isFixtureEditable(properties.fixture, true)}
                placeholder={""}
                onChange={onHomeParticipantsChangedHandler}
                disabledOptionValues={homeParticipantsDisabledOptionValues}
              />
            </Form.Item>

            <Form.Item
              name={"awayParticipants"}
              label={formatMessage({
                id: "fixtureListPage.upsertFixtureModel.form.awayParticipants",
              })}
              tooltip={formatMessage({ id: "form.help.fixture.away.persons" })}
            >
              <SelectPersons
                disabled={!isEmpty(properties.fixture) && !isFixtureEditable(properties.fixture, true)}
                placeholder={""}
                onChange={onAwayParticipantsChangedHandler}
                disabledOptionValues={awayParticipantsDisabledOptionValues}
              />
            </Form.Item>
          </>
        )}

        <Form.Item
          name={"venue"}
          label={formatMessage({
            id: "fixtureListPage.upsertFixtureModel.form.venue",
          })}
          tooltip={formatMessage({ id: "form.help.fixture.venue" })}
        >
          <SelectVenue disabled={!isEmpty(properties.fixture) && !isFixtureEditable(properties.fixture, true)} placeholder={""} />
        </Form.Item>

        <Form.Item
          name={"startDate"}
          label={formatMessage({
            id: "fixtureListPage.upsertFixtureModel.form.startDate",
          })}
          tooltip={formatMessage({ id: "form.help.fixture.date" })}
          style={{ marginBottom: "5px" }}
        >
          <DatePicker
            disabled={!isEmpty(properties.fixture) && !isFixtureEditable(properties.fixture, true)}
            size={"middle"}
            format={"DD/MM/YYYY"}
          />
        </Form.Item>

        <Form.Item
          name={"startTime"}
          label={formatMessage({
            id: "fixtureListPage.upsertFixtureModel.form.startTime",
          })}
          tooltip={formatMessage({ id: "form.help.fixture.time" })}
        >
          <TimePicker
            disabled={!isEmpty(properties.fixture) && !isFixtureEditable(properties.fixture, true)}
            size={"middle"}
            format={"HH:mm"}
          />
        </Form.Item>
        <Form.Item
          name={"fixtureType"}
          label={formatMessage({
            id: "fixtureListPage.upsertFixtureModel.form.fixtureType",
          })}
          tooltip={formatMessage({ id: "form.help.fixture.type" })}
          style={{ marginBottom: "5px" }}
        >
          <Select
            options={sportConfiguration.fixtureTypes.map((x) => ({
              label: x,
              value: x,
            }))}
          />
        </Form.Item>

        <Form.Item
          name={"status"}
          label={formatMessage({
            id: "fixtureListPage.upsertFixtureModel.form.status",
          })}
          tooltip={formatMessage({ id: "form.help.status.item" }, { item: formatMessage({ id: "fixture" }) })}
        >
          <Select
            options={sportConfiguration.fixtureStatuses.map((x) => ({
              label: x,
              value: x,
            }))}
          />
        </Form.Item>

        <Form.Item
          name={"stage"}
          label={formatMessage({
            id: "fixtureListPage.upsertFixtureModel.form.stage",
          })}
          tooltip={formatMessage({ id: "form.help.fixture.stage" })}
          style={{ marginBottom: "5px" }}
        >
          <SelectStage disabled={!isEmpty(properties.fixture) && !isFixtureEditable(properties.fixture, true)} placeholder={""} />
        </Form.Item>

        <Form.Item
          name={"pool"}
          label={formatMessage({
            id: "fixtureListPage.upsertFixtureModel.form.pool",
          })}
          tooltip={formatMessage({ id: "form.help.fixture.pool" })}
          style={{ marginBottom: "5px" }}
        >
          <SelectPool disabled={!isEmpty(properties.fixture) && !isFixtureEditable(properties.fixture, true)} placeholder={""} />
        </Form.Item>

        <Form.Item
          name={"series"}
          label={formatMessage({
            id: "fixtureListPage.upsertFixtureModel.form.series",
          })}
          tooltip={formatMessage({ id: "form.help.fixture.series" })}
          style={{ marginBottom: "5px" }}
        >
          <SelectSeries disabled={!isEmpty(properties.fixture) && !isFixtureEditable(properties.fixture, true)} placeholder={""} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
